/**
 * 어드민 사이드바 메뉴 정의 (단일 소스)
 *
 * Sidebar 렌더링과 '메뉴 관리'(pages/MenuSettings) 화면이 같은 정의를 공유한다.
 * 항목 식별 키는 `to` 경로를 그대로 쓴다.
 */

import type { ReactNode } from 'react'
import {
  ClipboardList, Cpu, FlaskConical, Puzzle, Blocks, Braces,
  LayoutTemplate, Loader, Type, Image, Sparkles, Music, Film,
  Database, RefreshCw, ShieldBan, Replace, Link2, Tag, BarChart3, Workflow,
  MessageCircleQuestion, MessagesSquare, Activity, Images, UserSquare, Mail, Star, Ticket, Coins,
  CreditCard, Wallet, Store, Send, ScrollText, Settings2, LayoutDashboard, Eye, TrendingUp,
  Route, StickyNote, FileText, ScanFace, Menu,
} from 'lucide-react'

export const ICON_SIZE = 16

export interface NavItem {
  to: string
  label: string
  icon: ReactNode
  devOnly?: boolean   // dev 서버에서만 노출 (prod admin 숨김)
  hidden?: boolean    // 기본 미노출. '메뉴 관리'에서 다시 켤 수 있고, 라우트/페이지는 유지
  locked?: boolean    // '메뉴 관리'에서 끌 수 없음 (스스로를 잠그는 사고 방지)
}

export interface NavSubGroup {
  subgroup: string
  items: NavItem[]
}

export interface NavGroup {
  group: string
  items: (NavItem | NavSubGroup)[]
}

export function isSubGroup(item: NavItem | NavSubGroup): item is NavSubGroup {
  return 'subgroup' in item
}

export const NAV_ITEMS: NavGroup[] = [
  {
    group: '운영',
    items: [
      { to: '/ops/dashboard', label: '대시보드', icon: <LayoutDashboard size={ICON_SIZE} /> },
      { to: '/ops/users', label: '회원', icon: <UserSquare size={ICON_SIZE} /> },
      { to: '/ops/invitations', label: '청첩장', icon: <ClipboardList size={ICON_SIZE} /> },
      { to: '/ops/invitation-views', label: '조회 분석', icon: <Eye size={ICON_SIZE} />, hidden: true }, // 저사용 정리
      { to: '/ai/contact-inquiries', label: '고객 문의', icon: <Mail size={ICON_SIZE} />, hidden: true }, // 저사용 정리
    ],
  },
  {
    group: '상품',
    items: [
      { to: '/system/products', label: '상품/가격', icon: <Tag size={ICON_SIZE} /> },
    ],
  },
  {
    group: '결제·커머스',
    items: [
      { to: '/commerce/revenue', label: '매출 관리', icon: <TrendingUp size={ICON_SIZE} /> },
      { to: '/commerce/settlements', label: '정산 내역', icon: <Wallet size={ICON_SIZE} />, hidden: true }, // 저사용 정리
      { to: '/commerce/payments', label: '결제 내역', icon: <CreditCard size={ICON_SIZE} />, hidden: true }, // 매출 관리/Toss에 흡수
      { to: '/commerce/analytics', label: 'GA4 분석', icon: <BarChart3 size={ICON_SIZE} /> },
      { to: '/commerce/attribution', label: '유입·광고 분석', icon: <Route size={ICON_SIZE} /> }, // 광고 ROAS 탭 통합
      { to: '/commerce/revenue-reports', label: '매출 리포트', icon: <FileText size={ICON_SIZE} /> },
      { to: '/commerce/marketing-links', label: '마케팅 링크', icon: <Link2 size={ICON_SIZE} /> },
      { to: '/wedding/smartstore-links', label: '스토어 링크', icon: <Link2 size={ICON_SIZE} /> },
      { to: '/commerce/smartstore-events', label: '스마트스토어 이벤트', icon: <Store size={ICON_SIZE} /> },
      { to: '/commerce/product-search', label: '상품 가격 분석', icon: <Tag size={ICON_SIZE} /> },
      { to: '/commerce/activation-codes', label: '활성화 코드', icon: <Ticket size={ICON_SIZE} /> },
      { to: '/commerce/credit-transactions', label: '크레딧 거래', icon: <Coins size={ICON_SIZE} /> },
      { to: '/commerce/toss', label: 'Toss 결제', icon: <Wallet size={ICON_SIZE} /> },
    ],
  },
  {
    group: '메모',
    items: [
      { to: '/ops/memos', label: 'PA코드 관리', icon: <StickyNote size={ICON_SIZE} />, hidden: true }, // 저사용 정리
    ],
  },
  {
    group: 'AI',
    items: [
      { to: '/ai/history', label: '히스토리', icon: <ClipboardList size={ICON_SIZE} /> },
      { to: '/ai/gallery', label: '사진 갤러리', icon: <Images size={ICON_SIZE} /> },
      { to: '/ai/reference-photos', label: '유저 참조사진', icon: <UserSquare size={ICON_SIZE} /> },
      { to: '/ai/models', label: 'AI 모델', icon: <Cpu size={ICON_SIZE} /> },
      { to: '/ai/pipeline', label: '파이프라인', icon: <Workflow size={ICON_SIZE} />, hidden: true }, // 정적 문서 페이지, 방치
      { to: '/ai/prompt-blocks', label: '프롬프트 블록', icon: <Puzzle size={ICON_SIZE} /> },
      { to: '/ai/prompt-stats', label: '프롬프트 통계', icon: <BarChart3 size={ICON_SIZE} />, hidden: true }, // 프롬프트 블록에 사용량 중복
      { to: '/ai/quality-ratings', label: '사진 별점', icon: <Star size={ICON_SIZE} /> },
      { to: '/ai/reference-photo-quality', label: '참조사진 품질', icon: <ScanFace size={ICON_SIZE} /> },
      { to: '/system/ai-queue', label: 'AI 잡 큐', icon: <Activity size={ICON_SIZE} /> },
    ],
  },
  {
    group: '콘텐츠',
    items: [
      { to: '/wedding/templates', label: '템플릿', icon: <LayoutTemplate size={ICON_SIZE} /> },
      { to: '/wedding/compose-templates', label: '조립 템플릿', icon: <Blocks size={ICON_SIZE} /> },
      { to: '/wedding/section-designs', label: '섹션 라이브러리', icon: <LayoutTemplate size={ICON_SIZE} /> },
      { to: '/wedding/template-variables', label: '템플릿 변수', icon: <Braces size={ICON_SIZE} /> },
      { to: '/wedding/loading-designs', label: '로딩 디자인', icon: <Loader size={ICON_SIZE} /> },
      { to: '/wedding/thankyou-designs', label: '감사장 디자인', icon: <Mail size={ICON_SIZE} /> },
      { to: '/ai/conversion-references', label: 'Liquid 레퍼런스', icon: <Replace size={ICON_SIZE} />, hidden: true }, // import 셋업 니치
      {
        subgroup: '에셋',
        items: [
          // 에셋 5종은 저사용 정리로 기본 숨김. 필요 시 '메뉴 관리'에서 켠다.
          { to: '/wedding/fonts', label: '폰트', icon: <Type size={ICON_SIZE} />, hidden: true },
          { to: '/wedding/images', label: '이미지', icon: <Image size={ICON_SIZE} />, hidden: true },
          { to: '/wedding/icons', label: '아이콘', icon: <Sparkles size={ICON_SIZE} />, hidden: true },
          { to: '/wedding/music', label: '음악', icon: <Music size={ICON_SIZE} />, hidden: true },
          { to: '/wedding/videos', label: '영상', icon: <Film size={ICON_SIZE} />, hidden: true },
        ],
      },
    ],
  },
  {
    group: '챗봇',
    items: [
      { to: '/ai/chatbot-faqs', label: '챗봇 FAQ', icon: <MessageCircleQuestion size={ICON_SIZE} /> },
      { to: '/ai/chatbot-sessions', label: '챗봇 대화 로그', icon: <MessagesSquare size={ICON_SIZE} /> },
    ],
  },
  {
    group: '시스템',
    items: [
      { to: '/system/settings', label: '설정', icon: <Settings2 size={ICON_SIZE} /> },
      { to: '/system/menus', label: '메뉴 관리', icon: <Menu size={ICON_SIZE} />, locked: true },
      { to: '/system/profanity', label: '욕설 필터', icon: <ShieldBan size={ICON_SIZE} /> },
      { to: '/system/solapi-log', label: 'Solapi 발송 로그', icon: <Send size={ICON_SIZE} /> },
      { to: '/system/audit-logs', label: '감사 로그', icon: <ScrollText size={ICON_SIZE} /> },
      { to: '/ai/test', label: 'AI 테스트', icon: <FlaskConical size={ICON_SIZE} />, devOnly: true },
      { to: '/system/db-reset', label: 'DB 초기화', icon: <Database size={ICON_SIZE} />, devOnly: true },
      { to: '/system/sync', label: '상용 동기화', icon: <RefreshCw size={ICON_SIZE} />, devOnly: true },
    ],
  },
]
